import Experience from "@folio-2026/experience"
import { EVENTS } from "@folio-2026/shared"
import { gsap } from "gsap"
import {
  cameraPosition,
  color,
  float,
  Fn,
  instancedBufferAttribute,
  length,
  positionWorld,
  shapeCircle,
  smoothstep,
  texture,
  time,
  uniform,
  vec3,
} from "three/tsl"
import * as THREE from "three/webgpu"
import { FolderApi } from "tweakpane"

export default class Grid {
  scene: THREE.Scene
  debugFolder?: FolderApi
  experience: Experience
  n: number
  geometry!: THREE.BufferGeometry
  pointsMaterial?: THREE.PointsNodeMaterial
  lineMaterial?: THREE.LineBasicNodeMaterial
  instancedPoints?: THREE.Sprite
  lineSegments?: THREE.LineSegments
  perlin!: THREE.Texture
  progress!: THREE.UniformNode<number>
  debugCube?: THREE.Mesh

  settings = {
    xSize: 20,
    ySize: 10,
    zSize: 40,
    zStart: 15,
    color: uniform(color("#ffffff")),
    minDistance: uniform(1), // comincia a sfumare da
    maxDistance: uniform(8), // finisce di sfumare a
    hideUntilDistance: uniform(0.5), // nasconde il grid fino a questa distanza
    spacing: 1,
    intensity: uniform(3.2),
    pointsIntensity: uniform(2.5),
    pointsEmissiveness: uniform(0.4),
    lineEmissiveness: uniform(0.15),
    pointSize: uniform(3.5),
    timeScale: uniform(0.01),
    textureScale: uniform(0.2),
    fadeOutPosition: { x: 0, y: 2, z: 4.5 },
    fadeOutDistanceMin: uniform(1.5), // distance from fadeOutPosition where grid becomes invisible
    fadeOutDistanceMax: uniform(3.5), // distance from fadeOutPosition where grid becomes invisible
  }

  constructor({
    scene,
    debugFolder,
    spacing,
    gridColor,
    perlin,
  }: {
    scene: THREE.Scene
    debugFolder?: FolderApi
    spacing: number
    gridColor: THREE.Color
    perlin: THREE.Texture
  }) {
    this.experience = Experience.getInstance()
    this.scene = scene
    this.debugFolder = debugFolder

    this.progress = uniform(0.5)
    this.settings = {
      ...this.settings,
      spacing,
    }

    if (gridColor) {
      this.settings.color = uniform(color(`#${gridColor.getHexString()}`))
    }

    this.n = this.settings.xSize * this.settings.ySize * this.settings.zSize

    this.perlin = perlin

    this.setMesh()
    this.setDebug()
    this.setEventListeners()
  }

  setEventListeners() {
    this.experience.on(EVENTS.Guestbook.BeforeEnter, () => {
      this.progress.value = 0
    })

    this.experience.on(
      EVENTS.Guestbook.Entering,
      this.onEnterAnimation.bind(this),
    )
  }

  onEnterAnimation(event: unknown) {
    const { progress } = event as { progress: number }
    if (progress === 1 && this.progress.value < 1) {
      gsap.to(this.progress, {
        value: 1,
        duration: 5,
        ease: "power2.out",
      })
    }
  }

  setMesh() {
    // Clean up existing objects
    if (this.instancedPoints) {
      this.scene.remove(this.instancedPoints)
      const material = this.instancedPoints.material
      if (Array.isArray(material)) {
        material.forEach((m) => m.dispose())
      } else {
        material.dispose()
      }
    }
    if (this.lineSegments) {
      this.scene.remove(this.lineSegments)
      this.lineSegments.geometry.dispose()
      const material = this.lineSegments.material
      if (Array.isArray(material)) {
        material.forEach((m) => m.dispose())
      } else {
        material.dispose()
      }
    }

    this.geometry = new THREE.BufferGeometry()

    const { xSize, spacing, zStart, color, ySize, zSize } = this.settings
    const xOffset = (xSize - 1) / 2

    const positions = []
    for (let i = 0; i < this.n; i++) {
      const { x, y, z } = this.mapTo3D(i)
      // Center x coordinates around 0, with half on negative and half on positive side
      // z starts at zStart and goes in descending order (zStart-1, zStart-2, etc.)
      // Apply spacing to position points at the correct distance
      positions.push(
        (x - xOffset) * spacing,
        y * spacing,
        (zStart - z) * spacing,
      )
    }

    const positionAttribute = new THREE.InstancedBufferAttribute(
      new Float32Array(positions),
      3,
    )

    // Create random intensity values for each point (between 0.5 and 1.5)
    const randomIntensities = []
    for (let i = 0; i < this.n; i++) {
      randomIntensities.push(0.5 + Math.random() * 1.0) // Range: 0.5 to 1.5
    }
    const intensityAttribute = new THREE.InstancedBufferAttribute(
      new Float32Array(randomIntensities),
      1,
    )

    // Use the uniform color node directly
    this.pointsMaterial = new THREE.PointsNodeMaterial({
      colorNode: color,
      positionNode: instancedBufferAttribute(positionAttribute),
      sizeNode: this.settings.pointSize,
      vertexColors: true,
      sizeAttenuation: false,
      alphaToCoverage: true,
    })

    // Sample perlin noise texture using world position coordinates
    // Scale the position to get appropriate texture sampling (adjust scale as needed)
    // Add offsets based on all coordinates to create variation between sibling lines
    const noiseZOffset = positionWorld.z.mul(float(0.5))
    const noiseYOffset = positionWorld.y.mul(float(0.3))
    const noiseXOffset = positionWorld.x.mul(float(0.2))
    const noiseOffset = noiseZOffset.add(noiseYOffset).add(noiseXOffset)
    const textureUV = positionWorld.xy
      .mul(this.settings.textureScale)
      .add(noiseOffset)
    const perlinValue = texture(
      this.perlin,
      textureUV.add(time.mul(this.settings.timeScale)),
    )
      .r.pow(3)
      .mul(this.settings.intensity)

    const perlin = perlinValue

    // Get random intensity per point
    const randomIntensity = instancedBufferAttribute(intensityAttribute)
    const pointsPerlin = perlin
      .mul(randomIntensity)
      .mul(this.settings.pointsIntensity)

    // Calculate normalized z position (0 to 1) along the grid's z-axis
    const zMin = float(
      (this.settings.zStart - (this.settings.zSize - 1)) *
        this.settings.spacing,
    )
    const zMax = float(this.settings.zStart * this.settings.spacing)
    const zRange = zMax.sub(zMin)
    const normalizedZ = positionWorld.z.sub(zMin).div(zRange)

    const diff = normalizedZ.sub(this.progress)
    const zBasedOpacity = smoothstep(0, 0.05, diff).oneMinus()
    const loading = this.progress.mul(zBasedOpacity)

    // Calculate fade-out based on distance from fadeOutPosition
    const fadePositionVec = vec3(
      this.settings.fadeOutPosition.x,
      this.settings.fadeOutPosition.y,
      this.settings.fadeOutPosition.z,
    )
    const distanceFromFadePosition = length(positionWorld.sub(fadePositionVec))
    // Fade out from 0 to fadeOutDistance: 0 = invisible, fadeOutDistance = fully visible
    const fadeOutFactor = smoothstep(
      this.settings.fadeOutDistanceMin,
      this.settings.fadeOutDistanceMax,
      distanceFromFadePosition,
    )

    // Distance-based opacity for points (reused in opacityNode and emissiveNode)
    const pointsDistanceFromCamera = length(positionWorld.sub(cameraPosition))
    const pointsFarOpacity = smoothstep(
      this.settings.minDistance,
      this.settings.maxDistance,
      pointsDistanceFromCamera,
    ).oneMinus()
    const pointsCloseOpacity = smoothstep(
      this.settings.hideUntilDistance,
      this.settings.minDistance,
      pointsDistanceFromCamera,
    )
    const pointsDistanceOpacity = pointsFarOpacity.mul(pointsCloseOpacity)

    // Add emissiveNode to points material (like lines), faded by camera distance and progress
    ;(
      this.pointsMaterial as unknown as THREE.MeshPhysicalNodeMaterial
    ).emissiveNode = this.settings.color
      .mul(pointsPerlin.mul(this.settings.pointsEmissiveness))
      .mul(fadeOutFactor)
      .mul(pointsDistanceOpacity)
      .mul(loading)

    this.pointsMaterial.opacityNode = Fn(() => {
      const sphere = shapeCircle()
      const distanceFromCamera = length(positionWorld.sub(cameraPosition))
      const farOpacity = smoothstep(
        this.settings.minDistance,
        this.settings.maxDistance,
        distanceFromCamera,
      ).oneMinus()
      const closeOpacity = smoothstep(
        this.settings.hideUntilDistance,
        this.settings.minDistance,
        distanceFromCamera,
      )
      return sphere
        .mul(farOpacity)
        .mul(closeOpacity)
        .mul(loading)
        .mul(pointsPerlin)
        .mul(fadeOutFactor)
    })()

    this.instancedPoints = new THREE.Sprite(this.pointsMaterial)
    // Set the count property to specify number of instances
    this.instancedPoints.count = this.n
    this.instancedPoints.frustumCulled = false

    // this.instancedPoints.renderOrder = 9

    this.scene.add(this.instancedPoints)

    //
    // SET LINES
    //

    // Create a map to quickly find world positions from grid coordinates
    const positionMap = new Map<string, [number, number, number]>()
    for (let i = 0; i < this.n; i++) {
      const { x, y, z } = this.mapTo3D(i)
      const key = `${x},${y},${z}`
      // z starts at zStart and goes in descending order (zStart-1, zStart-2, etc.)
      // Apply spacing to position points at the correct distance
      const worldX = (x - xOffset) * spacing
      positionMap.set(key, [worldX, y * spacing, (zStart - z) * spacing])
    }

    const linePositions: number[] = []

    // For each point, connect to its neighbors on x, y, z axes
    for (let i = 0; i < this.n; i++) {
      const { x, y, z } = this.mapTo3D(i)
      const currentPos = positionMap.get(`${x},${y},${z}`)!

      // Check neighbor on +x axis
      if (x < xSize - 1) {
        const neighborPos = positionMap.get(`${x + 1},${y},${z}`)!
        linePositions.push(...currentPos, ...neighborPos)
      }

      // Check neighbor on +y axis
      if (y < ySize - 1) {
        const neighborPos = positionMap.get(`${x},${y + 1},${z}`)!
        linePositions.push(...currentPos, ...neighborPos)
      }

      // Check neighbor on +z axis
      if (z < zSize - 1) {
        const neighborPos = positionMap.get(`${x},${y},${z + 1}`)!
        linePositions.push(...currentPos, ...neighborPos)
      }
    }

    const lineGeometry = new THREE.BufferGeometry()
    lineGeometry.setAttribute(
      "position",
      new THREE.BufferAttribute(new Float32Array(linePositions), 3),
    )

    // Extract color value from uniform for line material
    this.lineMaterial = new THREE.LineBasicNodeMaterial({
      colorNode: this.settings.color,
      transparent: true,
    })
    ;(
      this.lineMaterial as unknown as THREE.MeshPhysicalNodeMaterial
    ).emissiveNode = this.settings.color
      .mul(perlin.mul(this.settings.lineEmissiveness))
      .mul(fadeOutFactor)

    this.lineMaterial.opacityNode = Fn(() => {
      const distanceFromCamera = length(positionWorld.sub(cameraPosition))

      // farOpacity: fades out as distance increases
      // When distance <= minDistance: fully visible (opacity = 1)
      // When distance >= maxDistance: invisible (opacity = 0)
      const farOpacity = smoothstep(
        this.settings.minDistance,
        this.settings.maxDistance,
        distanceFromCamera,
      ).oneMinus()
      const closeOpacity = smoothstep(
        this.settings.hideUntilDistance,
        this.settings.minDistance,
        distanceFromCamera,
      )

      return farOpacity
        .mul(closeOpacity)
        .mul(loading)
        .mul(perlin)
        .mul(fadeOutFactor)
    })()

    this.lineSegments = new THREE.LineSegments(lineGeometry, this.lineMaterial)

    this.scene.add(this.lineSegments)
  }

  setDebug() {
    if (!this.debugFolder) {
      return
    }

    const folder = this.debugFolder.addFolder({
      title: "🔲 Grid",
      expanded: false,
    })

    const settings = {
      color: `#${new THREE.Color(this.settings.color.value).getHexString()}`,
      intensity: this.settings.intensity.value,
      pointsIntensity: this.settings.pointsIntensity.value,
      pointsEmissiveness: this.settings.pointsEmissiveness.value,
      lineEmissiveness: this.settings.lineEmissiveness.value,
      pointSize: this.settings.pointSize.value,
      textureScale: this.settings.textureScale.value,
      zStart: this.settings.zStart,
      minDistance: this.settings.minDistance.value,
      maxDistance: this.settings.maxDistance.value,
      timeScale: this.settings.timeScale.value,
      fadeOutPositionX: this.settings.fadeOutPosition.x,
      fadeOutPositionY: this.settings.fadeOutPosition.y,
      fadeOutPositionZ: this.settings.fadeOutPosition.z,
      fadeOutDistanceMin: this.settings.fadeOutDistanceMin.value,
      fadeOutDistanceMax: this.settings.fadeOutDistanceMax.value,
    }

    folder
      .addBinding(settings, "color", {
        view: "color",
      })
      .on("change", (ev) => {
        // Update the uniform color value
        this.settings.color.value = new THREE.Color(ev.value)
      })

    folder
      .addBinding(settings, "intensity", {
        min: 0,
        max: 10,
        step: 0.1,
        label: "Intensity",
      })
      .on("change", (ev) => {
        this.settings.intensity.value = ev.value
      })

    folder
      .addBinding(settings, "pointsIntensity", {
        min: 0,
        max: 10,
        step: 0.1,
        label: "Points Intensity",
      })
      .on("change", (ev) => {
        this.settings.pointsIntensity.value = ev.value
      })

    folder
      .addBinding(settings, "pointsEmissiveness", {
        min: 0,
        max: 1,
        step: 0.01,
        label: "Points Emissiveness",
      })
      .on("change", (ev) => {
        this.settings.pointsEmissiveness.value = ev.value
      })

    folder
      .addBinding(settings, "lineEmissiveness", {
        min: 0,
        max: 1,
        step: 0.01,
        label: "Line Emissiveness",
      })
      .on("change", (ev) => {
        this.settings.lineEmissiveness.value = ev.value
      })

    folder
      .addBinding(settings, "pointSize", {
        min: 0.5,
        max: 24,
        step: 0.5,
        label: "Point Size",
      })
      .on("change", (ev) => {
        this.settings.pointSize.value = ev.value
      })

    folder
      .addBinding(settings, "timeScale", {
        min: 0,
        max: 0.1,
        step: 0.001,
        label: "Time Scale",
      })
      .on("change", (ev) => {
        this.settings.timeScale.value = ev.value
      })

    folder
      .addBinding(settings, "textureScale", {
        min: 0,
        max: 1,
        step: 0.001,
        label: "Texture Scale",
      })
      .on("change", (ev) => {
        this.settings.textureScale.value = ev.value
      })

    folder
      .addBinding(settings, "zStart", {
        min: -100,
        max: 100,
        step: 0.1,
      })
      .on("change", () => {
        this.settings.zStart = settings.zStart
        // Rebuild mesh and lines with new zStart
        this.setMesh()
      })

    folder
      .addBinding(settings, "minDistance", {
        min: 0,
        max: 50,
        step: 0.1,
        label: "Min Distance",
      })
      .on("change", (ev) => {
        this.settings.minDistance.value = ev.value
      })

    folder
      .addBinding(settings, "maxDistance", {
        min: 0,
        max: 50,
        step: 0.1,
        label: "Max Distance",
      })
      .on("change", (ev) => {
        this.settings.maxDistance.value = ev.value
      })

    folder
      .addBinding(settings, "fadeOutPositionX", {
        min: -50,
        max: 50,
        step: 0.1,
        label: "Fade Out Position X",
      })
      .on("change", (ev) => {
        this.settings.fadeOutPosition.x = ev.value
        this.setMesh()
      })

    folder
      .addBinding(settings, "fadeOutPositionY", {
        min: -50,
        max: 50,
        step: 0.1,
        label: "Fade Out Position Y",
      })
      .on("change", (ev) => {
        this.settings.fadeOutPosition.y = ev.value
        this.setMesh()
      })

    folder
      .addBinding(settings, "fadeOutPositionZ", {
        min: -50,
        max: 50,
        step: 0.1,
        label: "Fade Out Position Z",
      })
      .on("change", (ev) => {
        this.settings.fadeOutPosition.z = ev.value
        this.setMesh()
      })

    folder
      .addBinding(settings, "fadeOutDistanceMin", {
        min: 0,
        max: 20,
        step: 0.1,
        label: "Fade Out Distance",
      })
      .on("change", (ev) => {
        this.settings.fadeOutDistanceMin.value = ev.value
      })
    folder
      .addBinding(settings, "fadeOutDistanceMax", {
        min: 0,
        max: 20,
        step: 0.1,
        label: "Fade Out Distance Max",
      })
      .on("change", (ev) => {
        this.settings.fadeOutDistanceMax.value = ev.value
      })
  }

  mapTo3D(i: number) {
    const { xSize, ySize } = this.settings

    const z = Math.floor(i / (xSize * ySize))
    i -= z * xSize * ySize
    const y = Math.floor(i / xSize)
    const x = i % xSize
    return { x: x, y: y, z: z }
  }

  mapFrom3D(x: number, y: number, z: number) {
    const { xSize, ySize } = this.settings

    return x + y * xSize + z * xSize * ySize
  }
}
